// frontend/app/components/lessonPlans/authoring/WizardStepConcepts.js
//
// Step 2: the concepts this lesson declares.
//
// These are the whole basis of Teacher Insights. The shipped question bank has
// no concept dimension, so mastery bars are computed only from checks tagged
// with one of these — a lesson that declares none gets no Insights at all.
// They are effectively the learning objectives, which is why the objectives can
// be turned into concepts in one press.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { Field, IconButton, SectionCard, TextField } from "./WizardFields";

//: Mirrors `services/lesson_shape.py` PLACEHOLDER_CONCEPT_VALUES. A concept
//: left with one of these names (lowercased, stripped) is the gap slide 13 of
//: the SME review showed: the first concept named, literally, `concept`.
const PLACEHOLDER_CONCEPT_VALUES = new Set(["concept", "key", "placeholder", "todo", ""]);

const looksLikePlaceholder = (value) =>
  PLACEHOLDER_CONCEPT_VALUES.has(String(value || "").trim().toLowerCase());

export const slugifyConcept = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

/** Make `base` unique against `taken` by suffixing _2, _3, … */
const uniqueKey = (base, taken) => {
  const seed = base || "concept";
  if (!taken.has(seed)) return seed;
  let n = 2;
  while (taken.has(`${seed}_${n}`)) n += 1;
  return `${seed}_${n}`;
};

const WizardStepConcepts = ({
  colors,
  concepts = [],
  objectives = [],
  onChange,
  errors = {},
  t,
}) => {
  const translate = t || ((key) => key);

  const update = (index, patch) =>
    onChange(concepts.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const remove = (index) => onChange(concepts.filter((_, i) => i !== index));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= concepts.length) return;
    const next = [...concepts];
    next[index] = concepts[target];
    next[target] = concepts[index];
    onChange(next);
  };

  const add = (label = "", objectiveIndex = null) => {
    const taken = new Set(concepts.map((c) => c.concept_key));
    onChange([
      ...concepts,
      {
        concept_key: uniqueKey(slugifyConcept(label), taken),
        label,
        objective_index: objectiveIndex,
      },
    ]);
  };

  const fillFromObjectives = () => {
    const taken = new Set(concepts.map((c) => c.concept_key));
    const existingLabels = new Set(
      concepts.map((c) => String(c.label || "").trim().toLowerCase())
    );

    const additions = [];
    objectives.forEach((objective, index) => {
      const label = String(objective || "").trim();
      if (!label) return;
      if (existingLabels.has(label.toLowerCase())) return;
      const key = uniqueKey(slugifyConcept(label), taken);
      taken.add(key);
      additions.push({ concept_key: key, label, objective_index: index });
    });

    if (additions.length) onChange([...concepts, ...additions]);
  };

  const usableObjectives = objectives.filter((o) => String(o || "").trim());

  return (
    <View>
      <SectionCard
        colors={colors}
        title={translate("Concepts")}
        subtitle={translate(
          "Concepts are what this lesson claims to teach. Every check you write is tagged to one, and the teacher's Insights panel shows one mastery bar per concept. A lesson with no concepts produces no Insights."
        )}
        icon="tag-multiple-outline"
        right={
          usableObjectives.length ? (
            <TouchableOpacity
              onPress={fillFromObjectives}
              accessibilityRole="button"
              accessibilityLabel={translate("Create from objectives")}
              style={[styles.fillButton, { borderColor: colors.accentPrimary }]}
            >
              <MaterialCommunityIcons
                name="auto-fix"
                size={14}
                color={colors.accentPrimary}
              />
              <Text style={[styles.fillText, { color: colors.accentPrimary }]}>
                {translate("Create from objectives")}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      >
        {errors.concepts ? (
          <Text style={[styles.error, { color: colors.danger }]}>{translate(errors.concepts)}</Text>
        ) : null}

        {concepts.length === 0 ? (
          <Text style={[styles.empty, { color: colors.textMuted }]}>
            {translate(
              "No concepts yet. Without them this lesson will have no Insights."
            )}
          </Text>
        ) : null}

        {concepts.map((concept, index) => {
          const duplicate =
            concepts.filter((c) => c.concept_key === concept.concept_key).length > 1;
          const placeholder =
            looksLikePlaceholder(concept.concept_key) || looksLikePlaceholder(concept.label);

          return (
            <View
              key={`concept-${index}`}
              style={[styles.conceptRow, { borderColor: colors.border }]}
            >
              <View style={styles.conceptRowMain}>
                <View style={styles.conceptFields}>
                  <Field colors={colors} label={translate("Shown to teachers")}>
                    <TextField
                      colors={colors}
                      value={concept.label}
                      onChange={(label) =>
                        update(index, {
                          label,
                          // Only auto-follow the label while the key is still the
                          // one we generated; once an author edits it by hand,
                          // rewriting it would break checks already tagged to it.
                          concept_key:
                            concept.concept_key === slugifyConcept(concept.label)
                              ? slugifyConcept(label)
                              : concept.concept_key,
                        })
                      }
                      placeholder={translate("A key idea in this lesson")}
                    />
                  </Field>

                  <Field
                    colors={colors}
                    label={translate("Key")}
                    error={
                      duplicate ? translate("Two concepts share this key") : undefined
                    }
                    hint={translate("Used to tag checks. Avoid changing it later.")}
                  >
                    <TextField
                      colors={colors}
                      value={concept.concept_key}
                      onChange={(concept_key) => update(index, { concept_key })}
                      placeholder="key_idea"
                      error={duplicate}
                    />
                  </Field>
                </View>

                <View style={styles.conceptActions}>
                  <IconButton
                    colors={colors}
                    icon="chevron-up"
                    label={translate("Move up")}
                    disabled={index === 0}
                    onPress={() => move(index, -1)}
                  />
                  <IconButton
                    colors={colors}
                    icon="chevron-down"
                    label={translate("Move down")}
                    disabled={index === concepts.length - 1}
                    onPress={() => move(index, 1)}
                  />
                  <IconButton
                    colors={colors}
                    icon="close"
                    label={`${translate("Remove concept")} ${index + 1}`}
                    danger
                    onPress={() => remove(index)}
                  />
                </View>
              </View>

              {placeholder ? (
                <Text style={[styles.placeholderWarning, { color: colors.warning }]}>
                  {translate(
                    "This looks like a placeholder — name the concept this check is really about."
                  )}
                </Text>
              ) : null}
            </View>
          );
        })}

        <TouchableOpacity
          onPress={() => add()}
          accessibilityRole="button"
          accessibilityLabel={translate("Add concept")}
          style={[styles.addButton, { borderColor: colors.borderStrong }]}
        >
          <MaterialCommunityIcons name="plus" size={15} color={colors.accentPrimary} />
          <Text style={[styles.addText, { color: colors.accentPrimary }]}>
            {translate("Add concept")}
          </Text>
        </TouchableOpacity>
      </SectionCard>
    </View>
  );
};

const styles = StyleSheet.create({
  conceptRow: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
  },
  conceptRowMain: { flexDirection: "row", alignItems: "flex-start", gap: 10 },
  conceptFields: { flex: 1, minWidth: 0 },
  conceptActions: { flexDirection: "row", gap: 4, marginTop: 22 },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 9,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 5,
  },
  addText: { fontSize: 12, fontWeight: "700" },
  fillButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
    gap: 5,
  },
  fillText: { fontSize: 11, fontWeight: "700" },
  empty: { fontSize: 12, fontStyle: "italic", marginBottom: 12 },
  error: { fontSize: 11, fontWeight: "600", marginBottom: 10 },
  placeholderWarning: { fontSize: 11, fontWeight: "600", marginTop: 8 },
});

export default WizardStepConcepts;
